import axiosInstance from "@/api/axiosInstance";
import {
  UpdateEnrollmentRequest,
  PathEnrollment,
  BatchUnenrollResponse,
} from "./types";

/**
 * Update an enrollment's details (currently the assignment due date).
 * PATCH /api/enrollments/:enrollmentId
 * Requires ROLE_LEARNING_ADMIN or ROLE_SUPER_ADMIN
 *
 * PATCH key-present semantics: `dueDate` only changes when the key is in the
 * body; explicit null clears the deadline (an undefined `dueDate` is dropped
 * by JSON serialization, leaving the deadline untouched).
 *
 * The backend returns the enriched enrollment row UNWRAPPED (same
 * denormalized shape as the GET /api/learning-paths/{id}/enrollments
 * listing — no `{ data }` envelope) so it can be upserted verbatim into the
 * pathEnrollments normalized state.
 *
 * @param enrollmentId - ID of the enrollment to update
 * @param request - Update request payload containing dueDate
 * @returns Updated, enriched PathEnrollment row
 * @throws {Error} 400 Bad Request (invalid dueDate)
 * @throws {Error} 403 Forbidden (insufficient permissions)
 * @throws {Error} 404 Not Found (enrollment not found)
 * @throws {Error} 422 Unprocessable Entity (cannot update inactive enrollment)
 */
export const updateEnrollment = async (
  enrollmentId: number,
  request: UpdateEnrollmentRequest,
): Promise<PathEnrollment> => {
  const response = await axiosInstance.patch<PathEnrollment>(
    `/api/enrollments/${enrollmentId}`,
    {
      dueDate: request.dueDate,
    },
  );
  return response.data;
};

/**
 * Batch unenroll multiple employees from their learning paths
 * POST /api/enrollments/batch-unenroll
 * Requires ROLE_LEARNING_ADMIN or ROLE_SUPER_ADMIN
 *
 * @param enrollmentIds - Array of enrollment IDs to unenroll (1-50 items)
 * @param reason - Optional reason for batch unenrollment
 * @returns BatchUnenrollResponse with success/failed breakdown
 * @throws {Error} 400 Bad Request (empty array, too many items)
 * @throws {Error} 403 Forbidden (insufficient permissions)
 */
export const batchUnenroll = async (
  enrollmentIds: number[],
  reason?: string,
): Promise<BatchUnenrollResponse> => {
  const response = await axiosInstance.post<BatchUnenrollResponse>(
    `/api/enrollments/batch-unenroll`,
    {
      enrollmentIds,
      reason,
    },
  );
  return response.data;
};
